import SaveIcon from "@mui/icons-material/Save";
import { FloatingSaveBar } from "components/FloatingSaveBar";
import { Issue } from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";
import { hasCriticalIssues } from "util/forms";
import { useReportIsVerified } from "./useReportIsVerified";
type ReportFloatingSaveBarProps = {
  onSaveClick?: () => void;
  onCancel?: () => void;
  loading?: boolean;
  saveDisabled?: boolean;
  saving?: boolean;
  issues: Issue[];
  disabledWithAnyIssues?: boolean;
  saveText?: string;
  overrideSaveTooltip?: string;
  noIcon?: boolean;
  hideCancel?: boolean;
  saveStartIcon?: React.ReactNode;
};

export const ReportFloatingBar = ({
  onSaveClick,
  onCancel,
  saveDisabled,
  loading = false,
  saving = false,
  issues,
  disabledWithAnyIssues,
  saveText,
  overrideSaveTooltip,
  noIcon,
  hideCancel = false,
  saveStartIcon,
}: ReportFloatingSaveBarProps) => {
  const isVerified = useReportIsVerified();

  // No matter what if there are critical issues, we should disable the save
  // button Critical issues mean the data would not be saved to the DB so we
  // shouldn't even attempt to save it
  const disableDueToCriticalIssues = useMemo(
    () => hasCriticalIssues(issues),
    [issues]
  );

  const disableDueToAnyIssues = useMemo(
    () => disabledWithAnyIssues && issues.length > 0,
    [disabledWithAnyIssues, issues]
  );

  /**
   * This function is used to get the description of an issue within the context
   * of the report.
   *
   * We want to append the relevant record name to the issue message so it's easier
   * to track down the source of the issue. For nested records, we want to show the
   * parent record name if it exists.
   *
   *  Example:
   *  - An error on a chemical would show `EHS mixture chemicals must have at
   *    least one component with an EHS CAS number (Lead-Acid Battery)`
   *  - And error on a component within the chemical will use the chemical name
   *    as the parent name: `EHS chemical component must have an EHS CAS number (Lead-Acid Battery)`
   */
  const getIssueDescription = useCallback((issue: Issue) => {
    const baseMessage = issue.message;
    if (!issue.schemaStack) {
      return baseMessage;
    }

    // We don't want to show the facility name in the issue description, so we determine
    // the relevant segment depth.
    const facilitySegmentIndex = issue.schemaStack.findIndex(
      (segment) => segment.model === "Facility"
    );

    const rootSegmentIndex =
      facilitySegmentIndex === -1 ? 0 : facilitySegmentIndex;

    // If the issue is on the facility or report itself, we don't want to show
    // the name.
    if (issue.schemaStack.length <= rootSegmentIndex + 1) {
      return baseMessage;
    }

    let displaySegment = issue.schemaStack[rootSegmentIndex + 1];

    if (displaySegment.model === "FacilityContact") {
      // Show the Person name for FacilityContact issues
      displaySegment =
        issue.schemaStack[rootSegmentIndex + 2] ?? displaySegment;
    }

    // Otherwise we want to show the name of the facility or report's direct
    // descendant record, e.g. show the chemical name for a chemical component
    const issueName = displaySegment?.name;

    if (issueName) {
      return `${baseMessage} (${issueName})`;
    }
    return baseMessage;
  }, []);

  const tooltip = useMemo(
    () =>
      overrideSaveTooltip
        ? overrideSaveTooltip
        : disableDueToCriticalIssues || disableDueToAnyIssues
        ? "Resolve compliance issues to proceed."
        : undefined,
    [overrideSaveTooltip, disableDueToCriticalIssues, disableDueToAnyIssues]
  );
  const _saveDisabled = useMemo(
    () =>
      loading ||
      saveDisabled ||
      disableDueToCriticalIssues ||
      disableDueToAnyIssues,
    [saveDisabled, loading, disableDueToCriticalIssues, disableDueToAnyIssues]
  );
  if (isVerified) {
    return null;
  }
  let icon = saveStartIcon;
  // If we want an icon but none was provided use a default of saveIcon
  if (!noIcon && !saveStartIcon) {
    icon = <SaveIcon />;
  }
  return (
    <FloatingSaveBar
      hideCancel={hideCancel}
      onSaveClick={onSaveClick}
      onCancel={onCancel}
      saving={saving}
      saveText={saveText ?? "Save"}
      saveDisabled={_saveDisabled}
      saveStartIcon={icon}
      issues={issues}
      saveTooltipTitle={tooltip}
      getIssueDescription={getIssueDescription}
    />
  );
};
